import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Image,
  Video,
  ArrowLeft,
  Loader2,
  Trash2,
  Edit,
  X,
  Upload,
  Link2,
  Instagram,
  Twitter,
  Eye,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { value: "telegram", label: "Telegram", uploadType: "media" },
  { value: "privacy", label: "Privacy", uploadType: "media" },
  { value: "onlyfans", label: "OnlyFans", uploadType: "media" },
  { value: "twitter", label: "X / Twitter", uploadType: "media" },
  { value: "instagram", label: "Instagram", uploadType: "link" },
  { value: "tiktok", label: "TikTok", uploadType: "link" },
  { value: "kwai", label: "Kwai", uploadType: "link" },
];

const AdGalleryPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const fileInputRef = useRef(null);

  // Form state
  const [formPlatform, setFormPlatform] = useState("telegram");
  const [formTitle, setFormTitle] = useState("");
  const [formCaption, setFormCaption] = useState("");
  const [formMediaUrls, setFormMediaUrls] = useState([]);
  const [formMediaType, setFormMediaType] = useState("image");
  const [formExternalLink, setFormExternalLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAds();
  }, [selectedPlatform]);

  const fetchAds = async () => {
    setLoading(true);
    try {
      let url = `${API}/ads/gallery?Authorization=Bearer ${token}`;
      if (selectedPlatform) {
        url += `&platform=${selectedPlatform}`;
      }
      const response = await axios.get(url);
      setAds(response.data.ads || []);
    } catch (error) {
      console.error("Failed to fetch ads:", error);
      toast.error("Erro ao carregar anúncios");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check limits
    if (formMediaType === "video" && files.length > 1) {
      toast.error("Apenas 1 vídeo por anúncio");
      return;
    }
    if (formMediaType === "image" && (formMediaUrls.length + files.length) > 3) {
      toast.error("Máximo 3 fotos por anúncio");
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(
          `${API}/upload/profile?Authorization=Bearer ${token}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      setFormMediaUrls([...formMediaUrls, ...urls]);
      toast.success("Upload concluído!");
    } catch (error) {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (index) => {
    setFormMediaUrls(formMediaUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formTitle.trim()) {
      toast.error("Título obrigatório");
      return;
    }

    const platformInfo = PLATFORMS.find(p => p.value === formPlatform);
    if (platformInfo?.uploadType === "media" && formMediaUrls.length === 0) {
      toast.error("Adicione pelo menos uma mídia");
      return;
    }
    if (platformInfo?.uploadType === "link" && !formExternalLink) {
      toast.error("Link da mídia obrigatório");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/ads?Authorization=Bearer ${token}`,
        {
          platform: formPlatform,
          title: formTitle,
          caption: formCaption,
          media_urls: formMediaUrls,
          media_type: formMediaType,
          external_link: formExternalLink,
          save_to_gallery: true
        }
      );
      toast.success("Anúncio salvo na galeria!");
      resetForm();
      setShowCreateModal(false);
      fetchAds();
    } catch (error) {
      toast.error("Erro ao salvar anúncio");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (adId) => {
    try {
      await axios.delete(`${API}/ads/${adId}?Authorization=Bearer ${token}`);
      toast.success("Anúncio removido");
      fetchAds();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const resetForm = () => {
    setFormPlatform("telegram");
    setFormTitle("");
    setFormCaption("");
    setFormMediaUrls([]);
    setFormMediaType("image");
    setFormExternalLink("");
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const groupAdsByPlatform = (adsList) => {
    const groups = {};
    adsList.forEach(ad => {
      if (!groups[ad.platform]) {
        groups[ad.platform] = [];
      }
      groups[ad.platform].push(ad);
    });
    return groups;
  };

  const groupedAds = groupAdsByPlatform(ads);

  const AdCard = ({ ad }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -5 }}
      className="bg-white/5 border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:border-white/20 transition-all"
      onClick={() => {
        setSelectedAd(ad);
        setShowPreviewModal(true);
      }}
    >
      {/* Media Preview */}
      <div className="aspect-square bg-white/5 relative">
        {ad.media_urls?.[0] ? (
          ad.media_type === "video" ? (
            <video
              src={getMediaUrl(ad.media_urls[0])}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={getMediaUrl(ad.media_urls[0])}
              alt={ad.title}
              className="w-full h-full object-cover"
            />
          )
        ) : ad.external_link ? (
          <div className="w-full h-full flex items-center justify-center">
            <Link2 className="w-8 h-8 text-white/30" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-8 h-8 text-white/30" />
          </div>
        )}
        
        {/* Media count badge */}
        {ad.media_urls?.length > 1 && (
          <Badge className="absolute top-2 right-2 bg-black/50 text-white border-0">
            +{ad.media_urls.length - 1}
          </Badge>
        )}
        
        {ad.media_type === "video" && (
          <Badge className="absolute top-2 left-2 bg-black/50 text-white border-0">
            <Video className="w-3 h-3 mr-1" />
            Vídeo
          </Badge>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <div className="font-medium text-sm truncate mb-1">{ad.title}</div>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs border-white/20 text-white/60">
            {ad.platform}
          </Badge>
          <span className="text-xs text-white/40">
            Usado {ad.usage_count || 0}x
          </span>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/profile")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Galeria de Anúncios</h1>
          </div>
          <Button
            className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
            onClick={openCreateModal}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Anúncio
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Platform Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={selectedPlatform === "" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedPlatform("")}
              className={selectedPlatform === "" ? "bg-[#FD5564]" : "border-white/20 text-white/70"}
            >
              Todos
            </Button>
            {PLATFORMS.map(p => (
              <Button
                key={p.value}
                variant={selectedPlatform === p.value ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPlatform(p.value)}
                className={selectedPlatform === p.value ? "bg-[#FD5564]" : "border-white/20 text-white/70"}
              >
                {p.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
            </div>
          ) : ads.length > 0 ? (
            selectedPlatform ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ads.map(ad => (
                  <AdCard key={ad.id} ad={ad} />
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedAds).map(([platform, platformAds]) => (
                  <div key={platform}>
                    <h2 className="text-lg font-semibold mb-4 capitalize">{platform}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {platformAds.map(ad => (
                        <AdCard key={ad.id} ad={ad} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-20">
              <Image className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-4">Nenhum anúncio salvo</p>
              <p className="text-white/30 text-sm mb-6">
                Crie anúncios para usar em suas trocas e divulgações
              </p>
              <Button
                className="bg-[#FD5564] hover:bg-[#FF7854]"
                onClick={openCreateModal}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Anúncio
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Anúncio</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Plataforma
              </label>
              <Select value={formPlatform} onValueChange={setFormPlatform}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Título do anúncio
              </label>
              <Input
                placeholder="Ex: Promo 30% OFF"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>

            {/* Media Upload or Link */}
            {PLATFORMS.find(p => p.value === formPlatform)?.uploadType === "media" ? (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Mídias (até 3 fotos ou 1 vídeo)
                </label>
                
                {/* Media Type Toggle */}
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={formMediaType === "image" ? "default" : "outline"}
                    onClick={() => {
                      setFormMediaType("image");
                      setFormMediaUrls([]);
                    }}
                    className={formMediaType === "image" ? "bg-[#FD5564]" : "border-white/20"}
                  >
                    <Image className="w-4 h-4 mr-1" />
                    Fotos
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={formMediaType === "video" ? "default" : "outline"}
                    onClick={() => {
                      setFormMediaType("video");
                      setFormMediaUrls([]);
                    }}
                    className={formMediaType === "video" ? "bg-[#FD5564]" : "border-white/20"}
                  >
                    <Video className="w-4 h-4 mr-1" />
                    Vídeo
                  </Button>
                </div>

                {/* Preview */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {formMediaUrls.map((url, index) => (
                    <div key={index} className="aspect-square relative rounded-lg overflow-hidden bg-white/10">
                      <img src={getMediaUrl(url)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeMedia(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {((formMediaType === "image" && formMediaUrls.length < 3) || 
                    (formMediaType === "video" && formMediaUrls.length < 1)) && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center hover:border-white/40 transition-all"
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-white/50 mb-1" />
                          <span className="text-xs text-white/50">Upload</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={formMediaType === "video" ? "video/*" : "image/*"}
                  multiple={formMediaType === "image"}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Link da mídia
                </label>
                <Input
                  placeholder="https://..."
                  value={formExternalLink}
                  onChange={(e) => setFormExternalLink(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
                <p className="text-xs text-white/40 mt-1">
                  Cole o link do post ou mídia existente
                </p>
              </div>
            )}

            {/* Caption */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Legenda do anúncio
              </label>
              <Textarea
                placeholder="🔥 Conteúdo exclusivo...&#10;&#10;✨ Acesse agora e ganhe desconto!"
                value={formCaption}
                onChange={(e) => setFormCaption(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[120px]"
              />
            </div>

            <Button
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Salvar na Galeria"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedAd?.title}</span>
              <Badge variant="outline" className="border-white/20 text-white/60">
                {selectedAd?.platform}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedAd && (
            <div className="space-y-4">
              {/* Media */}
              {selectedAd.media_urls?.length > 0 ? (
                <div className="aspect-video bg-white/5 rounded-lg overflow-hidden">
                  {selectedAd.media_type === "video" ? (
                    <video
                      src={getMediaUrl(selectedAd.media_urls[0])}
                      controls
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <img
                      src={getMediaUrl(selectedAd.media_urls[0])}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              ) : selectedAd.external_link && (
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 text-white/70">
                    <Link2 className="w-4 h-4" />
                    <a href={selectedAd.external_link} target="_blank" rel="noopener noreferrer" 
                       className="truncate hover:text-white">
                      {selectedAd.external_link}
                    </a>
                  </div>
                </div>
              )}

              {/* Caption */}
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="whitespace-pre-wrap text-white/80">
                  {selectedAd.caption || "Sem legenda"}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-white/50">
                <span>Usado {selectedAd.usage_count || 0} vezes</span>
                <span>Criado em {new Date(selectedAd.created_at).toLocaleDateString('pt-BR')}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedAd.caption || "");
                    toast.success("Legenda copiada!");
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Legenda
                </Button>
                <Button
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  onClick={() => {
                    handleDelete(selectedAd.id);
                    setShowPreviewModal(false);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdGalleryPage;
